import { useState } from 'react';

import { api } from '../lib/api';
import { Card } from './ui';

export const CURRENCIES = ['CAD', 'USD', 'EUR', 'AUD', 'GBP'] as const;

export interface ServerCost {
  monthlyCost: string | null;
  costCurrency: string | null;
  costNote: string | null;
  costUpdatedAt: string | null;
  costUpdatedBy: string | null;
}

/**
 * Formats money in its own currency, never converted.
 *
 * `Intl.NumberFormat` with the currency's own conventions, so AUD 1,200.00 and
 * EUR 1.200,00 both read the way their readers expect.
 */
export function money(amount: string | number | null | undefined, currency: string | null | undefined): string {
  if (amount === null || amount === undefined || !currency) return '—';
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);
  } catch {
    // An unrecognised currency code should still show the number.
    return `${n.toFixed(2)} ${currency}`;
  }
}

/**
 * The monthly lease cost of one server, editable in place.
 *
 * Typed by a person rather than read from a hosting provider: the estate spans
 * providers and some of it is leased, so a provider integration would cover part
 * of the cost and silently omit the rest. A partial cost total is worse than an
 * obviously manual one.
 */
export function CostCard({
  serverId,
  cost,
  canEdit,
  onSaved,
}: {
  serverId: string;
  cost: ServerCost;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(cost.monthlyCost ?? '');
  const [currency, setCurrency] = useState(cost.costCurrency ?? 'USD');
  const [note, setNote] = useState(cost.costNote ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (clear = false) => {
    setBusy(true);
    setError(null);
    try {
      await api(`/servers/${serverId}/cost`, {
        method: 'PATCH',
        body: JSON.stringify({
          monthlyCost: clear || amount === '' ? null : Number(amount),
          costCurrency: clear || amount === '' ? null : currency,
          costNote: note.trim() === '' ? null : note.trim(),
        }),
      });
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <Card
        title="Monthly cost"
        actions={
          canEdit ? (
            <button type="button" className="ghost" onClick={() => setEditing(true)}>
              {cost.monthlyCost === null ? 'Add cost' : 'Edit'}
            </button>
          ) : undefined
        }
      >
        {cost.monthlyCost === null ? (
          <p className="muted">
            No cost recorded. {canEdit ? 'Add one to include this server in the estate totals.' : ''}
          </p>
        ) : (
          <>
            <div className="cost-figure">
              <span className="stat-value">{money(cost.monthlyCost, cost.costCurrency)}</span>
              <span className="muted"> / month</span>
            </div>
            <p className="muted small">
              {money(Number(cost.monthlyCost) * 12, cost.costCurrency)} per year
            </p>
            {cost.costNote && <p className="notice">{cost.costNote}</p>}
            {cost.costUpdatedAt && (
              <p className="muted small">
                Last set by {cost.costUpdatedBy ?? 'someone'} on{' '}
                {new Date(cost.costUpdatedAt).toLocaleDateString()}
              </p>
            )}
          </>
        )}
      </Card>
    );
  }

  return (
    <Card title="Monthly cost">
      <div className="cost-form">
        <label>
          Amount
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
          />
        </label>
        <label>
          Currency
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="wide">
          Note
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What the rate covers, contract or renewal date"
          />
        </label>
      </div>

      {error && <p className="form-error" role="alert">{error}</p>}

      <div className="cost-actions">
        <button type="button" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="ghost" disabled={busy} onClick={() => setEditing(false)}>
          Cancel
        </button>
        {cost.monthlyCost !== null && (
          <button type="button" className="ghost" disabled={busy} onClick={() => void save(true)}>
            Clear
          </button>
        )}
      </div>
    </Card>
  );
}
